// client/src/components/common/PhoneCard.tsx
//
// One card per table row on a phone (ST-00207, m4b-contracts §5): the row's primary fields, a
// chevron, and the whole card as the target — the title is the link (or button) and its ::after
// covers the card, so a thumb anywhere on it opens the item while controls inside the card
// (a run chip, an Override button — PhoneCard `actions`) keep their own taps. Used by the status
// board, the approvals queue and the trips behind a dashboard figure. Styles: PhoneLayout.css.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import './PhoneLayout.css';

export type PhoneCardTone = 'critical' | 'warning' | 'notice';

export interface PhoneCardProps {
  /** The primary field (registration, trip reference) — the opener's text. */
  title: ReactNode;
  /** Opens the item at a route (a router Link) … */
  to?: string;
  /** … or runs an action (a button). Without either the card is not a target and has no chevron. */
  onOpen?: () => void;
  /** Accessible name of the opener when the title alone does not say what opens. */
  openLabel?: string;
  /** Top-right of the card: data age, an SLA countdown. */
  aside?: ReactNode;
  /** Left accent: a critical / warning row (as the desktop table's row accent). */
  tone?: PhoneCardTone | null;
  /** Live highlight (the row just changed). */
  highlight?: boolean;
  /** Secondary lines. */
  children?: ReactNode;
  /** Controls of their own under the lines (they sit above the card-wide opener). */
  actions?: ReactNode;
  /** Extra attributes for tests and the audit (data-*). */
  dataAttributes?: Record<`data-${string}`, string | undefined>;
}

const Chevron = () => (
  <svg className="phone-card__chevron" viewBox="0 0 16 16" fill="none" aria-hidden="true" data-testid="phone-card-chevron">
    <path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function PhoneCard({
  title,
  to,
  onOpen,
  openLabel,
  aside,
  tone,
  highlight = false,
  children,
  actions,
  dataAttributes,
}: PhoneCardProps) {
  const opens = !!to || !!onOpen;
  const className = [
    'phone-card',
    opens ? 'phone-card--opens' : '',
    tone ? `phone-card--${tone}` : '',
    highlight ? 'phone-card--highlight' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const opener = to ? (
    <Link to={to} className="phone-card__open" aria-label={openLabel}>
      {title}
    </Link>
  ) : onOpen ? (
    <button type="button" onClick={onOpen} className="phone-card__open" aria-label={openLabel}>
      {title}
    </button>
  ) : (
    <span className="phone-card__title">{title}</span>
  );

  return (
    <li className={className} {...dataAttributes}>
      <div className="phone-card__head">
        {opener}
        {aside != null && aside !== false && <span className="phone-card__aside">{aside}</span>}
      </div>
      {children != null && children !== false && <div className="phone-card__body">{children}</div>}
      {actions != null && actions !== false && <div className="phone-card__actions">{actions}</div>}
      {opens && <Chevron />}
    </li>
  );
}

/** The list the cards go in (a <ul>, named for screen readers). */
export function PhoneCardList({ label, children }: { label: string; children: ReactNode }) {
  return (
    <ul className="phone-card-list" aria-label={label}>
      {children}
    </ul>
  );
}
